import { useSearchParams, useNavigate } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { PageHeader } from "@/components/PageHeader";
import { getFriendlyErrorMessage } from "@/lib/api-client";
import { useCompareSimulations } from "@/features/simulations/hooks";
import { getConfidenceLevelLabel } from "@/lib/display-labels";
import { cn } from "@/lib/utils";

interface Metric {
  label: string;
  a: number | null;
  b: number | null;
  unit?: string;
  decimals?: number;
  hib?: boolean;
}

function fmt(v: number | null, decimals = 1, unit = ""): string {
  if (v === null || v === undefined) return "—";
  return `${v.toFixed(decimals)}${unit}`;
}

function deltaClass(a: number | null, b: number | null, hib = true): string {
  if (a === null || b === null || a === b) return "text-muted-foreground";
  const better = hib ? b > a : b < a;
  return better ? "text-status-running" : "text-status-stopped";
}

function deltaText(a: number | null, b: number | null, unit = ""): string {
  if (a === null || b === null) return "";
  const diff = b - a;
  const sign = diff > 0 ? "+" : "";
  return `${sign}${diff.toFixed(1)}${unit}`;
}

export function SimulationComparePage() {
  const [search] = useSearchParams();
  const navigate = useNavigate();
  const a = search.get("a");
  const b = search.get("b");
  const { data, isLoading, isError, error } = useCompareSimulations(a, b);

  if (!a || !b) {
    return (
      <Card>
        <CardContent className="pt-6 text-sm text-muted-foreground">
          Selecione duas simulações para comparar.
        </CardContent>
      </Card>
    );
  }
  if (isLoading) return <p className="text-sm text-muted-foreground">Carregando…</p>;
  if (isError || !data) {
    return (
      <Card>
        <CardContent className="pt-6 text-sm text-destructive">
          {isError ? getFriendlyErrorMessage(error) : "Falha ao carregar comparação."}
        </CardContent>
      </Card>
    );
  }

  const metrics: Metric[] = [
    { label: "OEE", a: data.a.oee, b: data.b.oee, unit: "%" },
    { label: "Disponibilidade", a: data.a.availability, b: data.b.availability, unit: "%" },
    { label: "Desempenho", a: data.a.performance, b: data.b.performance, unit: "%" },
    { label: "Qualidade", a: data.a.quality, b: data.b.quality, unit: "%" },
    {
      label: "Produção estimada",
      a: data.a.estimatedProduction,
      b: data.b.estimatedProduction,
      decimals: 0,
      unit: " un",
    },
  ];

  return (
    <section>
      <PageHeader
        title="Comparar simulações"
        subtitle="Side-by-side de cenários para apoiar decisão de produção."
        actions={
          <Button variant="outline" onClick={() => navigate("/simulations")}>
            <ArrowLeft className="h-4 w-4" />
            Voltar
          </Button>
        }
      />

      <div className="grid gap-4 md:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle className="text-base">A · {data.a.name}</CardTitle>
          </CardHeader>
          <CardContent className="text-xs text-muted-foreground">
            Confiança:{" "}
            {data.a.confidence ? getConfidenceLevelLabel(data.a.confidence as never) : "—"}
          </CardContent>
        </Card>
        <Card>
          <CardHeader>
            <CardTitle className="text-base">B · {data.b.name}</CardTitle>
          </CardHeader>
          <CardContent className="text-xs text-muted-foreground">
            Confiança:{" "}
            {data.b.confidence ? getConfidenceLevelLabel(data.b.confidence as never) : "—"}
          </CardContent>
        </Card>
      </div>

      <Card className="mt-4">
        <CardHeader>
          <CardTitle className="text-base">Métricas</CardTitle>
        </CardHeader>
        <CardContent className="overflow-x-auto p-0">
          <table className="w-full text-sm">
            <thead className="border-b bg-muted/30 text-xs uppercase tracking-wider text-muted-foreground">
              <tr>
                <th className="px-4 py-2 text-left">Métrica</th>
                <th className="px-4 py-2 text-right">A</th>
                <th className="px-4 py-2 text-right">B</th>
                <th className="px-4 py-2 text-right">Δ (B − A)</th>
              </tr>
            </thead>
            <tbody>
              {metrics.map((m) => (
                <tr key={m.label} className="border-b last:border-0">
                  <td className="px-4 py-2 font-medium">{m.label}</td>
                  <td className="px-4 py-2 text-right font-mono">{fmt(m.a, m.decimals, m.unit)}</td>
                  <td className="px-4 py-2 text-right font-mono">{fmt(m.b, m.decimals, m.unit)}</td>
                  <td
                    className={cn(
                      "px-4 py-2 text-right font-mono text-xs",
                      deltaClass(m.a, m.b, m.hib ?? true)
                    )}
                  >
                    {deltaText(m.a, m.b, m.unit)}
                  </td>
                </tr>
              ))}
              {/* Confiança POR cenário sempre visível (Princípio II MUST). */}
              <tr className="border-b bg-muted/20 last:border-0">
                <td className="px-4 py-2 font-medium">Confiança</td>
                <td className="px-4 py-2 text-right text-xs">
                  {data.a.confidence ? getConfidenceLevelLabel(data.a.confidence as never) : "—"}
                </td>
                <td className="px-4 py-2 text-right text-xs">
                  {data.b.confidence ? getConfidenceLevelLabel(data.b.confidence as never) : "—"}
                </td>
                <td className="px-4 py-2 text-right text-xs text-muted-foreground">—</td>
              </tr>
            </tbody>
          </table>
        </CardContent>
      </Card>

      <p className="mt-3 text-[11px] leading-snug text-muted-foreground">
        Δ = B − A (A é a âncora). Rodadas detalhadas modelam filas/bloqueios (DES); o preview leve é
        estimativa. Compare cenários com nível de confiança semelhante para decisões (Princípio II).
      </p>
    </section>
  );
}
